import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { map, Observable, of, Subject, switchMap } from 'rxjs';

import { AppConsts } from './app-const';
import { UserDetail } from 'app/models/user-detail.model';
import { UserRole } from 'app/models/user-role';
import { AppPrivileges } from '../enums/app-privileges.enum';
import { Roles } from '../enums/app-roles.enum';

@Injectable({
  providedIn: 'root',
})
export class UserConfigService implements Resolve<any> {
  public resolvePrivileges: Subject<string[]> = new Subject<string[]>();
  public resolveRoles: Subject<UserRole[]> = new Subject<UserRole[]>();

  constructor(private readonly httpClient: HttpClient) {}

  public resolve(): Observable<UserDetail> | Observable<never> {
    const url = `${AppConsts.webConfig.apiUrl}${AppConsts.webConfig.usersPath}/me`;

    if (AppConsts.loggedInUser) {
      return of(AppConsts.loggedInUser);
    }

    return this.httpClient.get<UserDetail>(url).pipe(
      map(response => {
        if (response) {
          AppConsts.loggedInUser = response;
          AppConsts.roles = response.roles;
          AppConsts.privileges = response.privileges as unknown as AppPrivileges[];
          AppConsts.locale = response.locale;
          AppConsts.timeZoneInfo = response.timeZone;
          this.resolvePrivileges.next(AppConsts.privileges);
          this.resolveRoles.next(AppConsts.roles);
        } else {
          return null;
        }
      }),
    );
  }

  public isAuthorized(permissionsToCheck: string[]): Observable<boolean> {
    let granted = false;
    return this.getPrivileges().pipe(
      switchMap(permissions => {
        if (permissions) {
          granted = permissions.some(item => permissionsToCheck.includes(item));
        }
        return of(granted);
      }),
    );
  }

  public getPrivileges(): Observable<string[]> {
    if (AppConsts.privileges !== undefined) {
      return of(AppConsts.privileges);
    }

    return this.resolvePrivileges.pipe(
      switchMap(permissions => {
        if (permissions) {
          return of(permissions);
        }
        return of(AppConsts.privileges);
      }),
    );
  }

  public getRoles(): Observable<UserRole[]> {
    if (AppConsts.roles !== undefined) {
      return of(AppConsts.roles);
    }

    return this.resolveRoles.pipe(
      switchMap(role => {
        if (role) {
          return of(role);
        }
        return of(AppConsts.roles);
      }),
    );
  }

  public get isInRoleAdmin(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.HK_ADMIN);
    }
    return granted;
  }

  public get isInRoleCostApprover(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.COST_APPROVER);
    }
    return granted;
  }

  public get isInRoleStoreOwner(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.STORE_OWNER);
    }
    return granted;
  }

  public get isInRoleMacdonaldsCorporate(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.MCDONALDS_CORPORATE);
    }
    return granted;
  }

  public get isInRoleStoreApprover(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.STORE_APPROVER);
    }
    return granted;
  }

  public get isInRoleServiceController(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.SERVICE_CONTROLLER);
    }
    return granted;
  }

  public get isInRoleMCDIENSTEN(): boolean {
    let granted = false;
    if (AppConsts.roles) {
      granted = AppConsts.roles.some(item => item.name === Roles.MCDIENSTEN);
    }
    return granted;
  }
}
